import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { LogOut, Home, Plus, Trash2, Mail, Zap, Eye, Power, Send, Clock } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const triggerLabels = {
  lead_created: 'New Lead Created',
  client_converted: 'Lead Converted to Client',
  intake_sent: 'Intake Form Sent',
  intake_completed: 'Intake Completed',
  booking_created: 'Booking Created',
  enrollment: 'Program Enrollment',
  registration: 'Event Registration',
};

export default function AdminAutomation() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [tplForm, setTplForm] = useState({ name: '', subject: '', body: '', template_type: 'general', trigger: '' });
  const [ruleForm, setRuleForm] = useState({ name: '', trigger_event: 'lead_created', action_type: 'send_email', action_config: { template_id: '' }, delay_minutes: 0 });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [tplRes, autoRes, logRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/email-templates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/automations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/email-log`, { withCredentials: true }),
      ]);
      setTemplates(tplRes.data);
      setAutomations(autoRes.data);
      setEmailLog(logRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/email-templates`, tplForm, { withCredentials: true });
      toast.success('Template created');
      setShowCreateTemplate(false);
      setTplForm({ name: '', subject: '', body: '', template_type: 'general', trigger: '' });
      fetchAll();
    } catch { toast.error('Failed to create template'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email-templates/${id}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const previewTemplate = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-templates/${id}/preview`, {}, { withCredentials: true });
      setShowPreview(res.data);
    } catch { toast.error('Failed to preview'); }
  };

  const createRule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/automations`, {
        ...ruleForm,
        delay_minutes: parseInt(ruleForm.delay_minutes) || 0
      }, { withCredentials: true });
      toast.success('Automation created');
      setShowCreateRule(false);
      setRuleForm({ name: '', trigger_event: 'lead_created', action_type: 'send_email', action_config: { template_id: '' }, delay_minutes: 0 });
      fetchAll();
    } catch { toast.error('Failed to create automation'); }
  };

  const toggleRule = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/automations/${id}/toggle`, {}, { withCredentials: true });
      toast.success(res.data.is_active ? 'Activated' : 'Deactivated');
      fetchAll();
    } catch { toast.error('Failed to toggle'); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this automation?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/automations/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <AdminLayout title="Automation"><div>
        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          {[{key:'templates',label:'Email Templates',icon:Mail},{key:'automations',label:'Automations',icon:Zap},{key:'log',label:'Email Log',icon:Send}].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${activeTab === t.key ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:bg-[#F1F5F9]'}`} data-testid={`tab-${t.key}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#0A0F1C]" data-testid="templates-title">Email Templates ({templates.length})</h2>
              <Button onClick={() => setShowCreateTemplate(true)} className="btn-primary-blue flex items-center gap-2" data-testid="create-template-btn"><Plus size={16} /> New Template</Button>
            </div>
            <div className="space-y-3">
              {templates.map((t, i) => (
                <div key={t.template_id} className="bg-white p-5 border border-[#E2E8F0] rounded-lg" data-testid={`template-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#0A0F1C]">{t.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{t.template_type}</span>
                        {t.trigger && <span className="text-xs px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1D4ED8]">{triggerLabels[t.trigger] || t.trigger}</span>}
                      </div>
                      <p className="text-sm text-[#475569]">Subject: {t.subject}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => previewTemplate(t.template_id)} className="btn-outline text-xs p-2" data-testid={`preview-tpl-${i}`}><Eye size={14} /></Button>
                      <Button onClick={() => deleteTemplate(t.template_id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-tpl-${i}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Automations Tab */}
        {activeTab === 'automations' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#0A0F1C]" data-testid="automations-title">Automations ({automations.length})</h2>
              <Button onClick={() => setShowCreateRule(true)} className="btn-primary-blue flex items-center gap-2" data-testid="create-automation-btn"><Plus size={16} /> New Automation</Button>
            </div>
            <div className="space-y-3">
              {automations.map((r, i) => (
                <div key={r.rule_id} className={`bg-white p-5 border rounded-lg ${r.is_active ? 'border-[#E2E8F0]' : 'border-[#E2E8F0] opacity-60'}`} data-testid={`automation-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#0A0F1C]">{r.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-[#F1F5F9] text-[#475569]'}`}>{r.is_active ? 'Active' : 'Paused'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-[#475569]">
                        <span>Trigger: {triggerLabels[r.trigger_event] || r.trigger_event}</span>
                        <span>Action: {r.action_type?.replace(/_/g, ' ')}</span>
                        {r.delay_minutes > 0 && <span className="flex items-center gap-1"><Clock size={12} /> {r.delay_minutes}m delay</span>}
                        <span>Runs: {r.executions}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => toggleRule(r.rule_id)} className={`p-2 rounded ${r.is_active ? 'text-green-600 hover:bg-green-50' : 'text-[#94A3B8] hover:bg-[#F1F5F9]'}`} data-testid={`toggle-auto-${i}`}><Power size={16} /></Button>
                      <Button onClick={() => deleteRule(r.rule_id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-auto-${i}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Log Tab */}
        {activeTab === 'log' && (
          <div>
            <h2 className="text-xl font-bold text-[#0A0F1C] mb-4" data-testid="log-title">Email Log ({emailLog.length})</h2>
            {emailLog.length > 0 ? (
              <div className="space-y-2">
                {emailLog.map((log, i) => (
                  <div key={log.log_id} className="bg-white p-4 border border-[#E2E8F0] rounded-lg flex items-center justify-between" data-testid={`email-log-${i}`}>
                    <div>
                      <span className="font-medium text-[#0A0F1C]">{log.subject}</span>
                      <div className="text-sm text-[#475569]">To: {log.to} | {new Date(log.sent_at).toLocaleString()}</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded bg-[#F1F5F9] text-[#475569]">{log.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white p-8 border border-[#E2E8F0] rounded-lg text-center">
                <Mail size={40} className="text-[#E2E8F0] mx-auto mb-3" />
                <p className="text-[#475569]">No emails sent yet</p>
              </div>
            )}
          </div>
        )}

        {/* Create Template Modal */}
        {showCreateTemplate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Create Email Template</h3>
              <form onSubmit={createTemplate} className="space-y-3">
                <Input placeholder="Template Name" value={tplForm.name} onChange={(e) => setTplForm({...tplForm, name: e.target.value})} required data-testid="tpl-name-input" />
                <Input placeholder="Email Subject" value={tplForm.subject} onChange={(e) => setTplForm({...tplForm, subject: e.target.value})} required />
                <Textarea placeholder="Email Body (use {{name}}, {{email}}, {{service}}, {{date}} as variables)" value={tplForm.body} onChange={(e) => setTplForm({...tplForm, body: e.target.value})} className="min-h-[150px]" required />
                <select value={tplForm.template_type} onChange={(e) => setTplForm({...tplForm, template_type: e.target.value})} className="form-input w-full">
                  <option value="general">General</option>
                  <option value="welcome">Welcome</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="intake_reminder">Intake Reminder</option>
                  <option value="nurture">Nurture</option>
                  <option value="custom">Custom</option>
                </select>
                <select value={tplForm.trigger || ''} onChange={(e) => setTplForm({...tplForm, trigger: e.target.value || null})} className="form-input w-full">
                  <option value="">No Auto-Trigger</option>
                  {Object.entries(triggerLabels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={() => setShowCreateTemplate(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Create</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create Automation Modal */}
        {showCreateRule && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Create Automation</h3>
              <form onSubmit={createRule} className="space-y-3">
                <Input placeholder="Automation Name" value={ruleForm.name} onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})} required data-testid="rule-name-input" />
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-1">Trigger Event</label>
                  <select value={ruleForm.trigger_event} onChange={(e) => setRuleForm({...ruleForm, trigger_event: e.target.value})} className="form-input w-full">
                    {Object.entries(triggerLabels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-1">Action Type</label>
                  <select value={ruleForm.action_type} onChange={(e) => setRuleForm({...ruleForm, action_type: e.target.value})} className="form-input w-full">
                    <option value="send_email">Send Email</option>
                    <option value="create_task">Create Task</option>
                    <option value="update_status">Update Status</option>
                    <option value="add_activity">Log Activity</option>
                  </select>
                </div>
                {ruleForm.action_type === 'send_email' && (
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-1">Email Template</label>
                    <select value={ruleForm.action_config.template_id || ''} onChange={(e) => setRuleForm({...ruleForm, action_config: { template_id: e.target.value }})} className="form-input w-full">
                      <option value="">Select template...</option>
                      {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                <Input type="number" placeholder="Delay (minutes)" value={ruleForm.delay_minutes} onChange={(e) => setRuleForm({...ruleForm, delay_minutes: e.target.value})} />
                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={() => setShowCreateRule(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Create</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Email Preview</h3>
              <div className="border border-[#E2E8F0] rounded-lg p-4 mb-4">
                <p className="text-sm text-[#94A3B8] mb-1">Subject</p>
                <p className="font-medium text-[#0A0F1C] mb-3">{showPreview.subject}</p>
                <p className="text-sm text-[#94A3B8] mb-1">Body</p>
                <pre className="text-sm text-[#475569] whitespace-pre-wrap font-sans">{showPreview.body}</pre>
              </div>
              <Button onClick={() => setShowPreview(null)} className="btn-outline w-full">Close</Button>
            </div>
          </div>
        )}
      </div></AdminLayout>);}
